import { DeleteOutlined, HolderOutlined, PlusOutlined, SwapOutlined, UploadOutlined } from '@ant-design/icons';
import * as productApi from '@apis/product';
import * as feedsV2Api from '@apis/feeds-v2';
import * as lastChanceApi from '@apis/last-chance';
import LastChanceStatusTag from '@components/LastChanceStatusTag';
import { useAuthentication } from '@hooks/authentication';
import { LastChanceProductListItem } from '@models/last-chance';
import { ProductModel } from '@models/product';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cdnImageUrl } from '@utils/image';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Empty,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  InputNumber,
  message,
  Switch,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title, Text } = Typography;

// ─── 타입 정의 ──────────────────────────────────────────────────────────────

interface BillboardEntry {
  _id: string;
  source: 'timesales' | 'lastchance';
}

interface BannerState {
  imageUrl: string;
  file?: File;
}

// ─── 할인상태 Tag 헬퍼 ───────────────────────────────────────────────────────

const DiscountStatusTag: React.FC<{ product: ProductModel }> = ({ product }) => {
  if (!product.applyPeriodDiscount) return <Tag color="default">-</Tag>;
  const endDate = product.discountInfo?.discountEndDate;
  if (endDate && new Date(endDate) < new Date()) return <Tag color="default">할인종료</Tag>;
  return <Tag color="green">할인중</Tag>;
};

const TimeSalesTypeTag: React.FC<{ type?: string }> = ({ type }) => {
  if (type === '오픈특가') return <Tag color="purple">오픈특가</Tag>;
  return <Tag color="blue">얼리버드</Tag>;
};

// ─── 빌보드 테이블용 드래그 가능한 Row ──────────────────────────────────────

interface SortableRowProps {
  id: string;
  children?: React.ReactNode;
  [key: string]: any;
}

const BillboardSortableRow: React.FC<SortableRowProps> = ({
  id,
  children,
  style: propStyle,
  onMouseEnter: propOnMouseEnter,
  onMouseLeave: propOnMouseLeave,
  ...props
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const [isHovered, setIsHovered] = useState(false);

  const style: React.CSSProperties = {
    ...propStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'grab',
    backgroundColor: isDragging ? '#e6f4ff' : isHovered ? '#f5f9ff' : undefined,
    boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.12)' : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      {...props}
      style={style}
      onMouseEnter={(e) => {
        setIsHovered(true);
        propOnMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        propOnMouseLeave?.(e);
      }}>
      {children}
    </tr>
  );
};

// ─── 빌보드 상품 선택 모달 ───────────────────────────────────────────────────

interface BillboardPickerModalProps {
  open: boolean;
  timeSalesProducts: ProductModel[];
  timeSalesProductTypeMap: Record<string, string>;
  lastChanceProducts: LastChanceProductListItem[];
  current: BillboardEntry[];
  onConfirm: (entries: BillboardEntry[]) => void;
  onClose: () => void;
}

const BillboardPickerModal: React.FC<BillboardPickerModalProps> = ({
  open,
  timeSalesProducts,
  timeSalesProductTypeMap,
  lastChanceProducts,
  current,
  onConfirm,
  onClose,
}) => {
  const [tsChecked, setTsChecked] = useState<Set<string>>(new Set());
  const [lcChecked, setLcChecked] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setTsChecked(new Set(current.filter((e) => e.source === 'timesales').map((e) => e._id)));
      setLcChecked(new Set(current.filter((e) => e.source === 'lastchance').map((e) => e._id)));
    }
  }, [open]);

  const toggleTs = (id: string) =>
    setTsChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleLc = (id: string) =>
    setLcChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleConfirm = () => {
    // 기존 순서 유지 + 신규 추가
    const prevTs = current.filter((e) => e.source === 'timesales' && tsChecked.has(e._id));
    const newTs = Array.from(tsChecked)
      .filter((id) => !prevTs.find((e) => e._id === id))
      .map((id) => ({ _id: id, source: 'timesales' as const }));
    const prevLc = current.filter((e) => e.source === 'lastchance' && lcChecked.has(e._id));
    const newLc = Array.from(lcChecked)
      .filter((id) => !prevLc.find((e) => e._id === id))
      .map((id) => ({ _id: id, source: 'lastchance' as const }));
    onConfirm([...prevTs, ...newTs, ...prevLc, ...newLc]);
  };

  const tsCols: ColumnsType<ProductModel> = [
    {
      title: '',
      width: 40,
      render: (_, r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={tsChecked.has(r._id)} onChange={() => toggleTs(r._id)} />
        </div>
      ),
    },
    {
      title: '썸네일',
      width: 52,
      render: (_, r) => (
        <img
          src={cdnImageUrl(r.thumbnailImage)}
          style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }}
        />
      ),
    },
    { title: '상품명', dataIndex: 'title' },
    { title: '판매자', dataIndex: 'sellerName', width: 110 },
    {
      title: '유형',
      width: 90,
      render: (_, r) => <TimeSalesTypeTag type={timeSalesProductTypeMap[r._id]} />,
    },
    {
      title: '가격',
      width: 110,
      render: (_, r) => {
        const d = r.applyPeriodDiscount && r.discountInfo?.discountedFee;
        return (
          <div style={{ fontSize: 12 }}>
            {d && <div style={{ textDecoration: 'line-through', color: '#999' }}>{r.fee?.toLocaleString()}원</div>}
            <div style={{ color: d ? '#e53e3e' : 'inherit', fontWeight: d ? 600 : 400 }}>
              {(d ? r.discountInfo?.discountedFee : r.fee)?.toLocaleString()}원
            </div>
          </div>
        );
      },
    },
    {
      title: '할인 기간',
      width: 180,
      render: (_, r) => {
        const { discountStartDate, discountEndDate } = r.discountInfo || {};
        if (!r.applyPeriodDiscount || (!discountStartDate && !discountEndDate))
          return <span style={{ color: '#bbb' }}>-</span>;
        return (
          <div style={{ fontSize: 11, lineHeight: '1.6' }}>
            {discountStartDate && <div>시작: {moment(discountStartDate).format('MM/DD HH:mm')}</div>}
            {discountEndDate && (
              <div style={{ color: new Date(discountEndDate) < new Date() ? '#e53e3e' : '#38a169' }}>
                종료: {moment(discountEndDate).format('MM/DD HH:mm')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '할인상태',
      width: 90,
      render: (_, r) => <DiscountStatusTag product={r} />,
    },
  ];

  const lcCols: ColumnsType<LastChanceProductListItem> = [
    {
      title: '',
      width: 40,
      render: (_, r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={lcChecked.has(r.id)} onChange={() => toggleLc(r.id)} />
        </div>
      ),
    },
    {
      title: '썸네일',
      width: 52,
      render: (_, r) =>
        r.thumbnailImage ? (
          <img
            src={cdnImageUrl(r.thumbnailImage)}
            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <div style={{ width: 36, height: 36, background: '#f0f0f0', borderRadius: 4 }} />
        ),
    },
    { title: '상품명', width: 150, render: (_, r) => r.displayTitle || r.productName },
    {
      title: '옵션',
      dataIndex: 'optionName',
      width: 140,
      ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: '연령',
      dataIndex: 'ageBandLabel',
      width: 80,
      render: (v) => v || '-',
    },
    {
      title: '판매기간',
      width: 100,
      render: (_, r) => {
        const { createdAt, deadlineAt } = r;
        if (!createdAt && !deadlineAt) return <span style={{ color: '#bbb' }}>-</span>;
        return (
          <div style={{ fontSize: 11, lineHeight: '1.6' }}>
            {createdAt && <div>시작: {moment(createdAt).format('MM/DD HH:mm')}</div>}
            {deadlineAt && (
              <div style={{ color: new Date(deadlineAt) < new Date() ? '#e53e3e' : '#38a169' }}>
                종료: {moment(deadlineAt).format('MM/DD HH:mm')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '가격',
      width: 100,
      align: 'center',
      render: (_, r) => {
        const hasDiscount = r.baseSalePrice != null && r.baseSalePrice !== r.lastChanceSalePrice;
        return (
          <div style={{ fontSize: 12 }}>
            {hasDiscount && (
              <div style={{ textDecoration: 'line-through', color: '#999' }}>{r.baseSalePrice?.toLocaleString()}원</div>
            )}
            <div style={{ color: hasDiscount ? '#e53e3e' : 'inherit', fontWeight: hasDiscount ? 600 : 400 }}>
              {(hasDiscount ? r.lastChanceSalePrice : r.baseSalePrice ?? r.lastChanceSalePrice)?.toLocaleString()}원
            </div>
          </div>
        );
      },
    },
    {
      title: '상태',
      width: 100,
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 11, color: '#666' }}>
            {r.purchasedCount ?? 0}/{r.recruitmentCapacity ?? 0}
          </span>
          <LastChanceStatusTag item={r} />
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title={`빌보드 상품 선택 (타임세일 ${tsChecked.size}개 · 라스트찬스 ${lcChecked.size}개)`}
      width={900}
      onCancel={onClose}
      onOk={handleConfirm}
      okText="적용"
      cancelText="취소"
      destroyOnClose>
      <Tabs
        items={[
          {
            key: 'timesales',
            label: `타임세일 (${timeSalesProducts.length}개)`,
            children: (
              <Table
                size="small"
                columns={tsCols}
                dataSource={timeSalesProducts}
                rowKey={(r) => r._id}
                pagination={false}
                scroll={{ y: 400 }}
                onRow={(r) => ({ onClick: () => toggleTs(r._id), style: { cursor: 'pointer' } })}
              />
            ),
          },
          {
            key: 'lastchance',
            label: `라스트찬스 (${lastChanceProducts.length}개)`,
            children: (
              <Table
                size="small"
                columns={lcCols}
                dataSource={lastChanceProducts}
                rowKey={(r) => r.id}
                pagination={false}
                scroll={{ y: 400 }}
                onRow={(r) => ({ onClick: () => toggleLc(r.id), style: { cursor: 'pointer' } })}
              />
            ),
          },
        ]}
      />
    </Modal>
  );
};

// ─── Deal Product 선택 모달 ──────────────────────────────────────────────────

interface DealProductPickerModalProps {
  open: boolean;
  timeSalesProducts: ProductModel[];
  timeSalesProductTypeMap: Record<string, string>;
  currentId: string | null;
  onConfirm: (id: string | null) => void;
  onClose: () => void;
}

const DealProductPickerModal: React.FC<DealProductPickerModalProps> = ({
  open,
  timeSalesProducts,
  timeSalesProductTypeMap,
  currentId,
  onConfirm,
  onClose,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSelectedId(currentId);
  }, [open, currentId]);

  const cols: ColumnsType<ProductModel> = [
    {
      title: '',
      width: 40,
      render: (_, r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Radio checked={selectedId === r._id} onChange={() => setSelectedId(r._id)} />
        </div>
      ),
    },
    {
      title: '썸네일',
      width: 52,
      render: (_, r) => (
        <img
          src={cdnImageUrl(r.thumbnailImage)}
          style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }}
        />
      ),
    },
    { title: '상품명', dataIndex: 'title' },
    { title: '판매자', dataIndex: 'sellerName', width: 110 },
    {
      title: '유형',
      width: 90,
      render: (_, r) => <TimeSalesTypeTag type={timeSalesProductTypeMap[r._id]} />,
    },
    {
      title: '가격',
      width: 110,
      render: (_, r) => {
        const d = r.applyPeriodDiscount && r.discountInfo?.discountedFee;
        return (
          <div style={{ fontSize: 12 }}>
            {d && <div style={{ textDecoration: 'line-through', color: '#999' }}>{r.fee?.toLocaleString()}원</div>}
            <div style={{ color: d ? '#e53e3e' : 'inherit', fontWeight: d ? 600 : 400 }}>
              {(d ? r.discountInfo?.discountedFee : r.fee)?.toLocaleString()}원
            </div>
          </div>
        );
      },
    },
    {
      title: '할인 기간',
      width: 180,
      render: (_, r) => {
        const { discountStartDate, discountEndDate } = r.discountInfo || {};
        if (!r.applyPeriodDiscount || (!discountStartDate && !discountEndDate))
          return <span style={{ color: '#bbb' }}>-</span>;
        return (
          <div style={{ fontSize: 11, lineHeight: '1.6' }}>
            {discountStartDate && <div>시작: {moment(discountStartDate).format('MM/DD HH:mm')}</div>}
            {discountEndDate && (
              <div style={{ color: new Date(discountEndDate) < new Date() ? '#e53e3e' : '#38a169' }}>
                종료: {moment(discountEndDate).format('MM/DD HH:mm')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '할인상태',
      width: 90,
      render: (_, r) => <DiscountStatusTag product={r} />,
    },
  ];

  return (
    <Modal
      open={open}
      title="하루 특가 선택 (타임세일 목록)"
      width={960}
      onCancel={onClose}
      onOk={() => onConfirm(selectedId)}
      okText="적용"
      cancelText="취소"
      destroyOnClose>
      <Table
        size="small"
        columns={cols}
        dataSource={timeSalesProducts}
        rowKey={(r) => r._id}
        pagination={false}
        scroll={{ y: 440 }}
        onRow={(r) => ({ onClick: () => setSelectedId(r._id), style: { cursor: 'pointer' } })}
      />
    </Modal>
  );
};

// ─── 배너 업로드 카드 ─────────────────────────────────────────────────────────

interface BannerUploadCardProps {
  label: string;
  banner: BannerState;
  onChange: (next: BannerState) => void;
}

// ─── 하루 특가 금액 + 할인 기간 표시 ──────────────────────────────────────────

const DealProductPriceInfo: React.FC<{ product: ProductModel; productType?: string }> = ({ product, productType }) => {
  const d = product.applyPeriodDiscount && product.discountInfo?.discountedFee;
  const { discountStartDate, discountEndDate } = product.discountInfo || {};
  const hasDateInfo = discountStartDate || discountEndDate;

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {d ? (
          <>
            <Text delete style={{ color: '#999', fontSize: 12 }}>
              {product.fee?.toLocaleString()}원
            </Text>
            <Tag color="red" style={{ margin: 0 }}>
              {product.discountInfo?.discountedFee?.toLocaleString()}원
            </Tag>
          </>
        ) : (
          <Tag color="blue" style={{ margin: 0 }}>
            {product.fee?.toLocaleString()}원
          </Tag>
        )}
        <TimeSalesTypeTag type={productType} />
        {product.applyPeriodDiscount && <DiscountStatusTag product={product} />}
      </div>
      {product.applyPeriodDiscount && hasDateInfo && (
        <div style={{ marginTop: 6, fontSize: 12, lineHeight: '1.7', color: '#666' }}>
          {discountStartDate && <div>할인 시작: {moment(discountStartDate).format('YYYY.MM.DD HH:mm')}</div>}
          {discountEndDate && (
            <div style={{ color: new Date(discountEndDate) < new Date() ? '#e53e3e' : '#38a169' }}>
              할인 종료: {moment(discountEndDate).format('YYYY.MM.DD HH:mm')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BannerUploadCard: React.FC<BannerUploadCardProps> = ({ label, banner, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    onChange({ imageUrl: previewUrl, file });
  };

  return (
    <Card
      size="small"
      title={<Text strong>{label}</Text>}
      style={{ flex: 1 }}
      extra={
        <div style={{ display: 'flex', gap: 8 }}>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              handleFile(file);
              return false;
            }}>
            <Button size="small" icon={<UploadOutlined />}>
              {banner.imageUrl ? '변경' : '이미지 선택'}
            </Button>
          </Upload>
          {banner.imageUrl && (
            <Button size="small" danger onClick={() => onChange({ imageUrl: '' })}>
              삭제
            </Button>
          )}
        </div>
      }>
      {banner.imageUrl ? (
        <img
          src={cdnImageUrl(banner.imageUrl)}
          alt={label}
          style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 6, background: '#f5f5f5' }}
        />
      ) : (
        <div
          style={{
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#bbb',
            border: '1px dashed #d9d9d9',
            borderRadius: 6,
            cursor: 'pointer',
          }}
          onClick={() => inputRef.current?.click()}>
          <div style={{ textAlign: 'center' }}>
            <UploadOutlined style={{ fontSize: 24, marginBottom: 8 }} />
            <div>이미지를 선택하세요</div>
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
    </Card>
  );
};

// ─── 메인 페이지 ─────────────────────────────────────────────────────────────

const DealsPage: React.FC = () => {
  useAuthentication();
  const queryClient = useQueryClient();

  // ── 상태 ────────────────────────────────────────────────────────────────

  const [billboardItems, setBillboardItems] = useState<BillboardEntry[]>([]);
  const [billboardAutoplay, setBillboardAutoplay] = useState(false);
  const [billboardDelay, setBillboardDelay] = useState(4);
  const [dealProductId, setDealProductId] = useState<string | null>(null);
  const [banners, setBanners] = useState<{ lastchance: BannerState; timesales: BannerState }>({
    lastchance: { imageUrl: '' },
    timesales: { imageUrl: '' },
  });

  const [timeSalesProducts, setTimeSalesProducts] = useState<ProductModel[]>([]);
  const [timeSalesProductTypeMap, setTimeSalesProductTypeMap] = useState<Record<string, string>>({});
  const [lastChanceProducts, setLastChanceProducts] = useState<LastChanceProductListItem[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  const [billboardPickerOpen, setBillboardPickerOpen] = useState(false);
  const [dealPickerOpen, setDealPickerOpen] = useState(false);

  // ── 데이터 조회 ─────────────────────────────────────────────────────────

  const configQuery = useQuery({
    queryKey: ['time-sales-config'],
    queryFn: () => feedsV2Api.getTimeSalesConfig(),
  });

  const timeSalesFeedQuery = useQuery({
    queryKey: ['time-sales-feed'],
    queryFn: () => feedsV2Api.getTimeSalesFeed(),
  });

  const lastChanceQuery = useQuery({
    queryKey: ['last-chance-products-for-deals'],
    queryFn: () => lastChanceApi.searchLastChanceProducts({ size: 300 }),
  });

  // ── 저장 mutation ────────────────────────────────────────────────────────

  const updateMutation = useMutation({
    mutationFn: feedsV2Api.updateTimeSalesConfig,
    onSuccess: () => {
      message.success('저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['time-sales-config'] });
    },
    onError: () => message.error('저장 중 오류가 발생했습니다.'),
  });

  // ── config 로드 ──────────────────────────────────────────────────────────

  useEffect(() => {
    const cfg = configQuery.data?.data;
    if (!cfg) return;

    const sections: any[] = cfg.data || [];

    // billboard
    const billboardSection = sections.find((s: any) => s.type === 'billboard');
    const billboardData = billboardSection?.data || [];
    setBillboardItems(billboardData.map((d: any) => ({ _id: d._id, source: d.type as 'timesales' | 'lastchance' })));
    setBillboardAutoplay(billboardSection?.autoplay ?? false);
    setBillboardDelay(billboardSection?.delay ?? 4);

    // deal product
    const dealData = sections.find((s: any) => s.type === 'deal-product')?.data || [];
    setDealProductId(dealData[0]?._id || null);

    // banners
    const bannerData: any[] = sections.find((s: any) => s.type === 'banners')?.data || [];
    setBanners({
      lastchance: { imageUrl: bannerData.find((b: any) => b.type === 'lastchance')?.imageUrl || '' },
      timesales: { imageUrl: bannerData.find((b: any) => b.type === 'timesales')?.imageUrl || '' },
    });
  }, [configQuery.data]);

  // ── 타임세일 상품 로드 ────────────────────────────────────────────────────

  useEffect(() => {
    const sections: any[] = timeSalesFeedQuery.data?.data?.data || [];
    const productListData = sections.find((s: any) => s.type === 'product-list')?.data || [];
    const savedIds = productListData.map((item: any) => item._id).filter(Boolean) as string[];

    const savedTypeMap: Record<string, string> = {};
    productListData.forEach((item: any) => {
      if (item._id) savedTypeMap[item._id] = item.type || '얼리버드';
    });
    setTimeSalesProductTypeMap(savedTypeMap);

    if (savedIds.length === 0) {
      setTimeSalesProducts([]);
      return;
    }

    setIsLoadingProducts(true);
    Promise.all(savedIds.map((id) => productApi.getProduct(id).then((r) => r.data)))
      .then((products) => setTimeSalesProducts(products.filter(Boolean)))
      .catch(() => message.error('타임세일 상품 정보를 불러오는 중 오류가 발생했습니다.'))
      .finally(() => setIsLoadingProducts(false));
  }, [timeSalesFeedQuery.data]);

  // ── 라스트찬스 상품 로드 ──────────────────────────────────────────────────

  useEffect(() => {
    const contents = lastChanceQuery.data?.data?.contents || [];
    setLastChanceProducts(contents);
  }, [lastChanceQuery.data]);

  // ── 저장 ─────────────────────────────────────────────────────────────────

  const handleSave = () => {
    const formData = new FormData();

    const bannerPayload = [
      {
        type: 'lastchance',
        imageUrl: banners.lastchance.file ? banners.lastchance.file.name : banners.lastchance.imageUrl,
      },
      {
        type: 'timesales',
        imageUrl: banners.timesales.file ? banners.timesales.file.name : banners.timesales.imageUrl,
      },
    ];

    if (banners.lastchance.file) {
      formData.append('files', banners.lastchance.file, banners.lastchance.file.name);
    }
    if (banners.timesales.file) {
      formData.append('files', banners.timesales.file, banners.timesales.file.name);
    }

    formData.append(
      'json',
      JSON.stringify({
        billboard: {
          data: billboardItems.map((b) => ({ _id: b._id, type: b.source })),
          autoplay: billboardAutoplay,
          delay: billboardDelay,
        },
        dealProductId,
        banners: bannerPayload,
      }),
    );

    updateMutation.mutate(formData);
  };

  // ── 빌보드 테이블에서 상품 정보 조회 ─────────────────────────────────────

  const tsProductMap = Object.fromEntries(timeSalesProducts.map((p) => [p._id, p]));
  const lcProductMap = Object.fromEntries(lastChanceProducts.map((p) => [p.id, p]));
  const dealProduct = dealProductId ? tsProductMap[dealProductId] : null;

  // ── 빌보드 테이블 컬럼 ───────────────────────────────────────────────────

  const sortableSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onBillboardSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIdx = billboardItems.findIndex((e) => `${e.source}-${e._id}` === active.id);
      const newIdx = billboardItems.findIndex((e) => `${e.source}-${e._id}` === over?.id);
      setBillboardItems((prev) => arrayMove(prev, oldIdx, newIdx));
    }
  };

  const billboardCols: ColumnsType<BillboardEntry> = [
    {
      title: '',
      width: 32,
      render: () => <HolderOutlined style={{ color: '#c0c0c0', fontSize: 15 }} />,
    },
    { title: '#', width: 44, render: (_, __, i) => i + 1 },
    {
      title: '출처',
      width: 90,
      render: (_, r) =>
        r.source === 'lastchance' ? (
          <Tag color="orange">라스트찬스</Tag>
        ) : (
          <TimeSalesTypeTag type={timeSalesProductTypeMap[r._id]} />
        ),
    },
    {
      title: '썸네일',
      width: 56,
      render: (_, r) => {
        const img =
          r.source === 'timesales' ? tsProductMap[r._id]?.thumbnailImage : lcProductMap[r._id]?.thumbnailImage;
        return img ? (
          <img src={cdnImageUrl(img)} style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 4 }} />
        ) : (
          <div style={{ width: 38, height: 38, background: '#f0f0f0', borderRadius: 4 }} />
        );
      },
    },
    {
      title: '상품명',
      render: (_, r) => {
        if (r.source === 'timesales') return tsProductMap[r._id]?.title || r._id;
        const lc = lcProductMap[r._id];
        if (!lc) return r._id;
        return [lc.displayTitle || lc.productName, lc.optionName, lc.ageBandLabel].filter(Boolean).join(' · ') || r._id;
      },
    },
    {
      title: '가격',
      width: 120,
      render: (_, r) => {
        if (r.source === 'timesales') {
          const p = tsProductMap[r._id];
          const d = p?.applyPeriodDiscount && p?.discountInfo?.discountedFee;
          return (
            <div style={{ fontSize: 12 }}>
              {d && <div style={{ textDecoration: 'line-through', color: '#999' }}>{p?.fee?.toLocaleString()}원</div>}
              <div style={{ color: d ? '#e53e3e' : 'inherit', fontWeight: d ? 600 : 400 }}>
                {(d ? p?.discountInfo?.discountedFee : p?.fee)?.toLocaleString()}원
              </div>
            </div>
          );
        }
        const lc = lcProductMap[r._id];
        const hasDiscount = lc?.baseSalePrice != null && lc.baseSalePrice !== lc.lastChanceSalePrice;
        return (
          <div style={{ fontSize: 12 }}>
            {hasDiscount && (
              <div style={{ textDecoration: 'line-through', color: '#999' }}>
                {lc?.baseSalePrice?.toLocaleString()}원
              </div>
            )}
            <div style={{ color: hasDiscount ? '#e53e3e' : 'inherit', fontWeight: hasDiscount ? 600 : 400 }}>
              {`${(hasDiscount
                ? lc?.lastChanceSalePrice
                : lc?.baseSalePrice ?? lc?.lastChanceSalePrice
              )?.toLocaleString()}원`}
            </div>
          </div>
        );
      },
    },
    {
      title: '할인 기간',
      width: 180,
      render: (_, r) => {
        if (r.source !== 'timesales') return <span style={{ color: '#bbb' }}>-</span>;
        const p = tsProductMap[r._id];
        if (!p?.applyPeriodDiscount) return <span style={{ color: '#bbb' }}>-</span>;
        const { discountStartDate, discountEndDate } = p.discountInfo || {};
        if (!discountStartDate && !discountEndDate) return <span style={{ color: '#bbb' }}>-</span>;
        return (
          <div style={{ fontSize: 11, lineHeight: '1.7' }}>
            {discountStartDate && <div>시작: {moment(discountStartDate).format('MM/DD HH:mm')}</div>}
            {discountEndDate && (
              <div style={{ color: new Date(discountEndDate) < new Date() ? '#e53e3e' : '#38a169' }}>
                종료: {moment(discountEndDate).format('MM/DD HH:mm')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '할인상태',
      width: 90,
      render: (_, r) => {
        if (r.source !== 'timesales') return <Tag color="default">-</Tag>;
        const p = tsProductMap[r._id];
        if (!p) return <Tag color="default">-</Tag>;
        return <DiscountStatusTag product={p} />;
      },
    },
    {
      title: '판매기간',
      width: 180,
      render: (_, r) => {
        if (r.source !== 'lastchance') return <span style={{ color: '#bbb' }}>-</span>;
        const lc = lcProductMap[r._id];
        if (!lc) return <span style={{ color: '#bbb' }}>-</span>;
        const { createdAt, deadlineAt } = lc;
        if (!createdAt && !deadlineAt) return <span style={{ color: '#bbb' }}>-</span>;
        return (
          <div style={{ fontSize: 11, lineHeight: '1.7' }}>
            {createdAt && <div>시작: {moment(createdAt).format('MM/DD HH:mm')}</div>}
            {deadlineAt && (
              <div style={{ color: new Date(deadlineAt) < new Date() ? '#e53e3e' : '#38a169' }}>
                종료: {moment(deadlineAt).format('MM/DD HH:mm')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '판매상태',
      width: 90,
      render: (_, r) => {
        if (r.source !== 'lastchance') return <Tag color="default">-</Tag>;
        const lc = lcProductMap[r._id];
        if (!lc) return <Tag color="default">-</Tag>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 11, color: '#666' }}>
              {lc.purchasedCount ?? 0}/{lc.recruitmentCapacity ?? 0}
            </span>
            <LastChanceStatusTag item={lc} />
          </div>
        );
      },
    },
    {
      title: '삭제',
      width: 60,
      render: (_, r) => (
        <Popconfirm
          title="빌보드에서 제거하시겠습니까?"
          onConfirm={() => setBillboardItems((prev) => prev.filter((e) => !(e._id === r._id && e.source === r.source)))}
          okText="제거"
          cancelText="취소">
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const isLoading =
    configQuery.isLoading || timeSalesFeedQuery.isLoading || lastChanceQuery.isLoading || isLoadingProducts;

  // ── 렌더링 ───────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 24 }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          특가탭 관리
        </Title>
        <Button type="primary" loading={updateMutation.isPending} onClick={handleSave}>
          저장
        </Button>
      </div>

      <Spin spinning={isLoading}>
        {/* ── 1. 빌보드 ──────────────────────────────────────────────── */}
        <Card
          title="빌보드 상품"
          style={{ marginBottom: 24 }}
          extra={
            <Button icon={<PlusOutlined />} onClick={() => setBillboardPickerOpen(true)}>
              상품 추가 / 편집
            </Button>
          }>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>자동 스와이프</span>
              <Switch
                checked={billboardAutoplay}
                onChange={setBillboardAutoplay}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, color: billboardAutoplay ? undefined : '#bbb' }}>스와이프 간격(초)</span>
              <InputNumber
                min={1}
                max={60}
                value={billboardDelay}
                disabled={!billboardAutoplay}
                onChange={(v) => setBillboardDelay(v ?? 4)}
                style={{ width: 80 }}
              />
            </div>
          </div>
          {billboardItems.length === 0 ? (
            <Empty description="상품을 추가하세요" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={onBillboardSortEnd}>
              <SortableContext
                items={billboardItems.map((e) => `${e.source}-${e._id}`)}
                strategy={verticalListSortingStrategy}>
                <Table
                  size="small"
                  columns={billboardCols}
                  dataSource={billboardItems}
                  rowKey={(r) => `${r.source}-${r._id}`}
                  pagination={false}
                  components={{
                    body: {
                      row: ({ 'data-row-key': id, ...rest }: any) => <BillboardSortableRow id={id} {...rest} />,
                    },
                  }}
                />
              </SortableContext>
            </DndContext>
          )}
        </Card>

        <Divider />

        {/* ── 2. Deal Product ─────────────────────────────────────── */}
        <Card
          title={
            <div>
              <div>하루 특가</div>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                미설정 혹은 기간만료시 웹에 노출되지않음
              </Text>
            </div>
          }
          style={{ marginBottom: 24 }}
          extra={
            <Button icon={<SwapOutlined />} onClick={() => setDealPickerOpen(true)}>
              {dealProduct ? '변경' : '상품 선택'}
            </Button>
          }>
          {dealProduct ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
              <img
                src={cdnImageUrl(dealProduct.thumbnailImage)}
                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{dealProduct.title}</div>
                <Text type="secondary">{dealProduct.sellerName}</Text>
                <DealProductPriceInfo product={dealProduct} productType={timeSalesProductTypeMap[dealProduct._id]} />
              </div>
              <Button
                style={{ marginLeft: 'auto' }}
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => setDealProductId(null)}>
                선택 해제
              </Button>
            </div>
          ) : (
            <Empty description="타임세일 목록에서 하루 특가을 선택하세요" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>

        <Divider />

        {/* ── 3. 배너 ─────────────────────────────────────────────── */}
        <Card title="배너" style={{ marginBottom: 24 }}>
          <Row gutter={24}>
            <Col span={12}>
              <BannerUploadCard
                label="라스트찬스 배너"
                banner={banners.lastchance}
                onChange={(next) => setBanners((prev) => ({ ...prev, lastchance: next }))}
              />
            </Col>
            <Col span={12}>
              <BannerUploadCard
                label="타임세일 배너"
                banner={banners.timesales}
                onChange={(next) => setBanners((prev) => ({ ...prev, timesales: next }))}
              />
            </Col>
          </Row>
        </Card>
      </Spin>

      {/* 빌보드 상품 선택 모달 */}
      <BillboardPickerModal
        open={billboardPickerOpen}
        timeSalesProducts={timeSalesProducts}
        timeSalesProductTypeMap={timeSalesProductTypeMap}
        lastChanceProducts={lastChanceProducts}
        current={billboardItems}
        onConfirm={(entries) => {
          setBillboardItems(entries);
          setBillboardPickerOpen(false);
        }}
        onClose={() => setBillboardPickerOpen(false)}
      />

      {/* 하루 특가 선택 모달 */}
      <DealProductPickerModal
        open={dealPickerOpen}
        timeSalesProducts={timeSalesProducts}
        timeSalesProductTypeMap={timeSalesProductTypeMap}
        currentId={dealProductId}
        onConfirm={(id) => {
          setDealProductId(id);
          setDealPickerOpen(false);
        }}
        onClose={() => setDealPickerOpen(false)}
      />
    </div>
  );
};

export default DealsPage;
